import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { Auth } from '../../../core/services/auth.service';
import { UbigeoService, UbigeoItem } from '../../../core/services/ubigeo.service';
import { PeruDatePipe } from '../../../shared/pipes/peru-date.pipe';
import { parseMoananiDateTime } from '../../../core/utils/peru-datetime';
import { OrderService } from '../../../core/services/order.service';
import { BoletaService, OrderBoletaStatus } from '../../../core/services/boleta.service';
import { Order } from '../../../shared/models/Order';
import { Estado } from '../../../shared/models/system';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    DialogModule, ButtonModule, InputTextModule, SelectModule, ToastModule, PeruDatePipe
  ],
  providers: [MessageService],
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css']
})
export class UserProfileComponent implements OnInit {

  user: any = {};
  userClone: any = {};

  pendingPaymentOrders: Order[] = [];
  activeOrders: Order[] = [];
  payingOrderId: number | null = null;
  historyOrders: Order[] = [];
  boletaStatusMap: Record<number, OrderBoletaStatus> = {};
  downloadingReceiptId: number | null = null;

  currentView: 'profile' | 'pending' | 'active' | 'history' = 'profile';

  showEditDialog: boolean = false;
  showOrderDetailsDialog: boolean = false;
  selectedOrder: Order | null = null;

  paises: UbigeoItem[] = [];
  departamentos: UbigeoItem[] = [];
  provincias: UbigeoItem[] = [];
  distritos: UbigeoItem[] = [];
  paisId: number | null = null;
  departamentoId: number | null = null;
  provinciaId: number | null = null;

  constructor(
    private auth: Auth,
    private ubigeo: UbigeoService,
    private orderService: OrderService,
    private boletaService: BoletaService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadUserProfile();
    this.loadUserOrders();
  }

  setView(view: 'profile' | 'pending' | 'active' | 'history') {
    this.currentView = view;
  }

  loadUserProfile() {
    this.auth.getUserInfoFromToken().subscribe({
      next: (data) => {
        this.user = data;
        this.cdr.detectChanges();
      },
      error: () => {
        this.messageService.add({severity:'error', summary:'Error', detail:'No se cargó el perfil'});
      }
    });
  }

  loadUserOrders() {
    this.orderService.getMyOrders().subscribe({
      next: (orders) => {
        const sorted = [...orders].sort(
          (a, b) =>
            (parseMoananiDateTime(b.createdAt)?.getTime() || 0) -
            (parseMoananiDateTime(a.createdAt)?.getTime() || 0)
        );

        this.pendingPaymentOrders = sorted.filter(o => !this.isHistoryOrder(o) && this.isPendingPaymentOrder(o));
        this.activeOrders = sorted.filter(o => !this.isHistoryOrder(o) && !this.isPendingPaymentOrder(o));
        this.historyOrders = sorted.filter(o => this.isHistoryOrder(o));
        this.cdr.detectChanges();
        this.loadMisBoletas();
      }
    });
  }

  loadMisBoletas() {
    if (!this.historyOrders.length) {
      this.boletaStatusMap = {};
      return;
    }
    this.boletaService.misBoletas().subscribe({
      next: (rows) => {
        this.boletaStatusMap = {};
        rows.forEach(r => { this.boletaStatusMap[r.orderId] = r; });
        this.cdr.detectChanges();
      },
      error: () => { this.boletaStatusMap = {}; }
    });
  }

  tieneBoleta(order: Order): boolean {
    return !!this.boletaStatusMap[order.id]?.tieneBoleta;
  }

  descargarRecibo(order: Order) {
    if (!this.tieneBoleta(order)) {
      this.messageService.add({ severity: 'info', summary: 'Recibo', detail: 'Boleta aún no emitida' });
      return;
    }
    this.downloadingReceiptId = order.id;
    this.boletaService.descargarMiBoleta(order.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.boletaStatusMap[order.id]?.numeroCompleto || 'boleta'}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.downloadingReceiptId = null;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.downloadingReceiptId = null;
        const detail = err?.status === 404 ? 'Boleta aún no emitida' : 'No se pudo descargar la boleta';
        this.messageService.add({ severity: err?.status === 404 ? 'info' : 'error', summary: 'Recibo', detail });
        this.cdr.detectChanges();
      }
    });
  }

  isHistoryOrder(order: Order): boolean {
    const status = (order.status || '').toUpperCase();
    return status === 'ENTREGADO' || status === 'CANCELADO';
  }

  isPendingPaymentOrder(order: Order): boolean {
    const payment = (order.paymentStatus || '').toUpperCase();
    return order.paymentChannel === 'MERCADOPAGO' && payment !== 'PAGO_COMPLETO';
  }

  payPendingOrder(order: Order) {
    this.payingOrderId = order.id;
    this.orderService.retryMercadoPagoPayment(order.id).subscribe({
      next: (status) => {
        if (status.initPoint) {
          window.location.href = status.initPoint;
          return;
        }
        this.payingOrderId = null;
        this.loadUserOrders();
      },
      error: (err) => {
        this.payingOrderId = null;
        this.messageService.add({
          severity: 'warn',
          summary: 'Pago',
          detail: err?.error?.message || 'El pago de este pedido ya no puede reintentarse'
        });
        this.loadUserOrders();
      }
    });
  }

  getOrderEstados(order: Order | null | undefined): Estado[] {
    return order?.estados?.length ? order.estados : [];
  }

  getOrderStatusFallback(order: Order | null | undefined): string {
    return order?.status || '';
  }

  getEstadoChipStyle(estado: Estado): Record<string, string> {
    const bg = estado.color || '#e5e7eb';
    return {
      'background-color': bg,
      color: estado.color ? '#fff' : '#4b5563'
    };
  }

  getStatusColorClass(status: string): string {
    const s = (status || '').toUpperCase();
    if (s === 'ENTREGADO') return 'status-success';
    if (s === 'CANCELADO') return 'status-danger';
    if (s === 'CONFIRMADO') return 'status-info';
    if (s === 'EN_RUTA' || s === 'LISTO_RECOJO') return 'status-purple';
    if (s === 'EN_PREPARACION' || s === 'PENDIENTE') return 'status-warning';
    return 'status-default';
  }

  openEditProfile() {
    this.userClone = { ...this.user };
    this.showEditDialog = true;
    this.ubigeo.getPaises().subscribe({
      next: (p) => {
        this.paises = p;
        if (this.user.distritoId) {
          this.loadUbigeoFromUser(this.user);
        } else {
          this.resetUbigeo();
        }
        this.cdr.detectChanges();
      }
    });
  }

  private resetUbigeo() {
    this.paisId = null;
    this.departamentoId = null;
    this.provinciaId = null;
    this.departamentos = [];
    this.provincias = [];
    this.distritos = [];
  }

  private loadUbigeoFromUser(u: any) {
    this.ubigeo.getDistritoConPadres(u.distritoId).subscribe({
      next: (padres) => {
        this.paisId = padres.paisId;
        this.departamentoId = padres.departamentoId;
        this.provinciaId = padres.provinciaId;
        this.userClone.distritoId = u.distritoId;
        this.ubigeo.getDepartamentos(padres.paisId).subscribe({
          next: (d) => {
            this.departamentos = d;
            this.ubigeo.getProvincias(padres.departamentoId).subscribe({
              next: (p) => {
                this.provincias = p;
                this.ubigeo.getDistritos(padres.provinciaId).subscribe({
                  next: (dis) => {
                    this.distritos = dis;
                    this.cdr.detectChanges();
                  }
                });
              }
            });
          }
        });
      }
    });
  }

  onPaisChange() {
    this.departamentoId = null;
    this.provinciaId = null;
    this.userClone.distritoId = null;
    this.provincias = [];
    this.distritos = [];
    if (this.paisId) {
      this.ubigeo.getDepartamentos(this.paisId).subscribe({
        next: (d) => { this.departamentos = d; this.cdr.detectChanges(); }
      });
    } else this.departamentos = [];
  }

  onDepartamentoChange() {
    this.provinciaId = null;
    this.userClone.distritoId = null;
    this.distritos = [];
    if (this.departamentoId) {
      this.ubigeo.getProvincias(this.departamentoId).subscribe({
        next: (p) => { this.provincias = p; this.cdr.detectChanges(); }
      });
    } else this.provincias = [];
  }

  onProvinciaChange() {
    this.userClone.distritoId = null;
    if (this.provinciaId) {
      this.ubigeo.getDistritos(this.provinciaId).subscribe({
        next: (d) => { this.distritos = d; this.cdr.detectChanges(); }
      });
    } else this.distritos = [];
  }

  saveProfileChanges() {
    const payload = {
      ...this.userClone,
      distritoId: this.userClone.distritoId ?? null,
      direccion: this.userClone.direccion ?? null
    };
    this.auth.updateUser(payload).subscribe({
      next: () => {
        this.showEditDialog = false;
        this.loadUserProfile();
        this.messageService.add({
          severity: 'success',
          summary: 'Actualizado',
          detail: 'Tus datos han sido guardados correctamente.'
        });
      },
      error: (err) => {
        const mensajeError = err.error?.message || 'Ocurrió un error al actualizar los datos.';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: mensajeError
        });
      }
    });
  }

  openOrderDetails(order: Order) {
    this.selectedOrder = order;
    this.showOrderDetailsDialog = true;
  }

  get addressLabel(): string {
    const parts = [
      this.user.direccion,
      this.user.distritoNombre,
      this.user.provinciaNombre,
      this.user.departamentoNombre
    ].filter(Boolean);
    return parts.length ? parts.join(', ') : 'Sin registrar';
  }
}
